import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import ExpandableGrid from '@prisjakt/ui-expandable-grid';
import ProductImage from '@prisjakt/ui-product-image';
import PriceLabel from '@prisjakt/ui-price-label';
import ProductCard from '@prisjakt/ui-product-card';
import MatchMedia from '../match-media';
import AlertButton from './components/alert-button';

import { productNavigation } from '../../areas/product/actions/product';
import {
  NEW_PRODUCT_CLICK,
  COMING_SOON_CLICK,
} from '../../globals/helpers/navigation-action';
import { filterCategories } from '../../areas/landing-page/components/product-recommendations/filter-categories';

import { Card, ImageAndDate, ExpectedPrice, ProductName } from './styles';

const NewProductReleases = props => {
  const { t } = useContext(AppContext);
  const { newReleases, preReleases, personalized } = props;

  if (
    !(newReleases && newReleases.nodes) ||
    !(preReleases && preReleases.nodes)
  ) {
    return '';
  }

  if (newReleases.nodes.length === 0 && preReleases.nodes.length === 0) {
    return '';
  }

  const newReleasesItems = filterCategories(newReleases.nodes);
  const preReleasesItems = filterCategories(preReleases.nodes);

  const newReleasesCards = newReleasesItems.map((product, index) => (
    <Card key={product.id}>
      <ProductCard
        name={<ProductName>{product.name}</ProductName>}
        image={
          product.media && (
            <ProductImage name={product.name} src={product.media.first} />
          )
        }
        price={product.priceSummary.regular}
        link={product.pathName}
        onClick={() => {
          props.productNavigation(NEW_PRODUCT_CLICK, index + 1);
        }}
        footer={
          <AlertButton
            product={product}
            text={t('product', 'alert.create-button')}
            type="PRICE_DROPS"
          />
        }
      />
    </Card>
  ));

  return (
    <div>
      {newReleasesItems.length > 0 && (
        <Panel
          header={
            <PanelHeader
              title={t('landing', 'personalized-releases.title')}
              subtitle={
                personalized
                  ? t('landing', 'personalized-releases.subtitle')
                  : ''
              }
            />
          }
        >
          <MatchMedia
            debounce={1000}
            serverSideRender={{ tablet: false }}
            matches={[
              {
                mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                values: { tablet: true },
              },
              {
                mediaQuery: 'all',
                values: { tablet: false },
              },
            ]}
          >
            {({ tablet }) => (
              <div>
                <ExpandableGrid
                  limit={tablet ? 3 : 4}
                  expandText={t('common', 'button.show-more')}
                  collapseText={t('common', 'button.show-less')}
                  items={newReleasesCards}
                />
              </div>
            )}
          </MatchMedia>
        </Panel>
      )}
      {preReleasesItems.length > 0 && (
        <Panel
          header={
            <PanelHeader
              title={t('landing', 'personalized-releases.pre-releases')}
            />
          }
        >
          <MatchMedia
            debounce={1000}
            serverSideRender={{ tablet: false }}
            matches={[
              {
                mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                values: { tablet: true },
              },
              {
                mediaQuery: 'all',
                values: { tablet: false },
              },
            ]}
          >
            {({ tablet }) => (
              <div>
                <ExpandableGrid
                  limit={tablet ? 3 : 4}
                  expandText={t('common', 'button.show-more')}
                  collapseText={t('common', 'button.show-less')}
                  items={preReleasesItems.map((product, index) => (
                    <Card key={product.id}>
                      <Link
                        to={product.pathName}
                        onClick={() => {
                          props.productNavigation(COMING_SOON_CLICK, index + 1);
                        }}
                      >
                        <ImageAndDate>
                          <ProductImage
                            name={product.name}
                            src={product.media.first}
                          />
                          <div>
                            <span>
                              {product.releaseDate ||
                                t(
                                  'landing',
                                  'personalized-releases.pre-releases'
                                )}
                            </span>
                          </div>
                        </ImageAndDate>
                        <ProductName>{product.name}</ProductName>
                        {product.expectedReleasePrice && (
                          <ExpectedPrice>
                            {t(
                              'landing',
                              'personalized-releases.expected-price'
                            )}{' '}
                            <PriceLabel value={product.expectedReleasePrice} />
                          </ExpectedPrice>
                        )}
                      </Link>
                      <AlertButton
                        product={product}
                        text={t('product', 'alert.create-button')}
                      />
                    </Card>
                  ))}
                />
              </div>
            )}
          </MatchMedia>
        </Panel>
      )}
    </div>
  );
};

NewProductReleases.propTypes = {
  newReleases: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        pathName: PropTypes.string,
        media: PropTypes.shape({
          first: PropTypes.string,
        }),
        priceSummary: PropTypes.shape({
          regular: PropTypes.number,
        }),
      })
    ),
  }),
  preReleases: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        pathName: PropTypes.string,
        releaseDate: PropTypes.string,
        expectedReleasePrice: PropTypes.number,
        media: PropTypes.shape({
          first: PropTypes.string,
        }),
      })
    ),
  }),
  productNavigation: PropTypes.func,
  personalized: PropTypes.bool,
};

NewProductReleases.defaultProps = {
  newReleases: { nodes: [] },
  preReleases: { nodes: [] },
  productNavigation: () => {},
  personalized: true,
};

export default connect(
  state => state,
  { productNavigation }
)(memo(NewProductReleases));
